import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

const TableContext = createContext();

// ===================================================================
// Initial state
// ===================================================================
const initialState = {
  // Table structure
  rows: 31,
  columns: 31,
  cells: {},

  // Table configuration
  tableSize: 'full',
  orientation: 'portrait',

  // UI state
  selectedCell: null,
  showExportModal: false,

  // Page management
  currentPage: 1,
  totalPages: 1,

  // Undo/Redo
  history: [],
  historyIndex: -1,

  // Header/Footer rows
  headerRows: [0],
  footerRows: [],

  // Column widths (sum ~100)
  columnWidths: [],
  labels: ['shop_name', 'date', 'customer_name'],
  // Product table constraints
  addedTable: false,               // user can add only one product table in the sheet
  showProductTableWizard: false,   // overlay to configure columns

  paperType:'a4',
  type:"billing_invoice"
};

// ===================================================================
// Helpers
// ===================================================================
const cellKey = (r, c) => `${r}-${c}`;

const isInside = (state, r, c) =>
  r >= 0 && r < state.rows && c >= 0 && c < state.columns;
const isMergeableCell = (cells, r, c) => {
  const k = cellKey(r, c);
  const cell = cells[k];
  return !!cell && !cell.merged;
};

const getSpan = (cell) => ({
  rs: Math.max(1, cell?.rowspan || 1),
  cs: Math.max(1, cell?.colspan || 1),
});

const canMergeRight = (state, cells, row, col) => {
  const main = cells[cellKey(row, col)];
  if (!main) return false;
  const { rs, cs } = getSpan(main);
  const targetCol = col + cs;
  if (targetCol >= state.columns) return false;
  for (let r = row; r < row + rs; r++) if (!isMergeableCell(cells, r, targetCol)) return false;
  return true;
};

const canMergeDown = (state, cells, row, col) => {
  const main = cells[cellKey(row, col)];
  if (!main) return false;
  const { rs, cs } = getSpan(main);
  const targetRow = row + rs;
  if (targetRow >= state.rows) return false;
  for (let c = col; c < col + cs; c++) if (!isMergeableCell(cells, targetRow, c)) return false;
  return true;
};

function normalizeWidths(list, cols) {
  let a = [...(list || [])].slice(0, cols).map(n => +n || 0);
  while (a.length < cols) a.push(0);
  const sum = a.reduce((s, x) => s + x, 0);
  if (sum <= 0) return Array.from({ length: cols }, () => +(100 / cols).toFixed(4));
  return a.map(x => +(100 * (x / sum)).toFixed(4));
}

const snapshotFromState = (s) => ({
  version: 1,
  meta: { savedAt: new Date().toISOString() },
  rows: s.rows,
  columns: s.columns,
  tableSize: s.tableSize,
  orientation: s.orientation,
  headerRows: s.headerRows,
  footerRows: s.footerRows,
  labels: s.labels || [],
  addedTable: !!s.addedTable,
  cells: s.cells,
  type: s.type,  
  paperType: s.paperType,
});

const isValidSnapshot = (snap) => {
  if (!snap || typeof snap !== 'object') return false;
  if (typeof snap.rows !== 'number' || typeof snap.columns !== 'number') return false;
  if (!snap.cells || typeof snap.cells !== 'object') return false;
  if (!Array.isArray(snap.headerRows) || !Array.isArray(snap.footerRows)) return false;
  return true;
};


// ===================================================================
// Generate initial cells (parameterized)
// ===================================================================
const generateInitialCells = (rows, columns) => {
  const cells = {};
  const firstRowHeightPct = 0; // hidden ruler row
  const bodyRows = Math.max(1, rows - 1);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const cellId = `${row}-${col}`;
      const isFirst = row === 0;
      const isHeader = row === 0;
      const isFooter = false;

      cells[cellId] = {
        id: cellId,
        row,
        col,
        content: '',
        styles: {
          fontWeight: (isHeader || isFooter) ? 'bold' : 'normal',
          backgroundColor: isHeader ? '#f9fafb' : isFooter ? '#f3f4f6' : '#ffffff',
          textAlign: 'left',
          verticalAlign: 'middle',
          padding: 0,
          textColor: '#000000',
          fontStyle: 'normal',
          textDecoration: 'none',
          widthPercent: 100 / columns,
          heightPercent: isFirst ? firstRowHeightPct : (100 - firstRowHeightPct) / bodyRows,
        },
        isHeader,
        isFooter,
        isFirst,
        colspan: 1,
        rowspan: 1,
        merged: false,
        parentCell: null,
      };
    }
  }
  return cells;
};

// ===================================================================
// Actions
// ===================================================================
const ACTIONS = {
  SET_TABLE_SIZE: 'SET_TABLE_SIZE',
  SET_ORIENTATION: 'SET_ORIENTATION',
  SET_TABLE_DIMENSIONS: 'SET_TABLE_DIMENSIONS',
  SELECT_CELL: 'SELECT_CELL',
  UPDATE_CELL_CONTENT: 'UPDATE_CELL_CONTENT',
  UPDATE_CELL_STYLE: 'UPDATE_CELL_STYLE',
  ADD_ROW: 'ADD_ROW',
  REMOVE_ROW: 'REMOVE_ROW',
  ADD_COLUMN: 'ADD_COLUMN',
  REMOVE_COLUMN: 'REMOVE_COLUMN',
  TOGGLE_EXPORT_MODAL: 'TOGGLE_EXPORT_MODAL',

  // merge/split
  MERGE_RIGHT: 'MERGE_RIGHT',
  MERGE_DOWN: 'MERGE_DOWN',
  UNMERGE_CELL: 'UNMERGE_CELL',
  SPLIT_CELL: 'SPLIT_CELL',
  SPLIT_CELL_DOWN: 'SPLIT_CELL_DOWN',

  // header/footer toggle
  TOGGLE_HEADER_ROW: 'TOGGLE_HEADER_ROW',
  TOGGLE_FOOTER_ROW: 'TOGGLE_FOOTER_ROW',

  // history
  UNDO: 'UNDO',
  REDO: 'REDO',
  SAVE_TO_HISTORY: 'SAVE_TO_HISTORY',
  LOAD_SNAPSHOT: 'LOAD_SNAPSHOT',

  // columns & widths
  SET_COLUMN_COUNT: 'SET_COLUMN_COUNT',
  SET_COLUMN_WIDTHS: 'SET_COLUMN_WIDTHS',

  // product table wizard
  OPEN_PRODUCT_TABLE_WIZARD: 'OPEN_PRODUCT_TABLE_WIZARD',
  CLOSE_PRODUCT_TABLE_WIZARD: 'CLOSE_PRODUCT_TABLE_WIZARD',
  ADD_PRODUCT_TABLE: 'ADD_PRODUCT_TABLE',
  DELETE_PRODUCT_TABLE : 'DELETE_PRODUCT_TABLE',

  //labels
  SET_LABELS: 'SET_LABELS',
  APPLY_LABEL_TO_CELL: 'APPLY_LABEL_TO_CELL',

  SET_TYPE: 'SET_TYPE',
  SET_PAPER_TYPE: 'SET_PAPER_TYPE',
};

// ===================================================================
// Reducer
// ===================================================================
function tableReducer(state, action) {
  switch (action.type) {
    // ------------------- table config -------------------
    case ACTIONS.SET_TABLE_SIZE:
      return { ...state, tableSize: action.payload };

    case ACTIONS.SET_ORIENTATION:
      return { ...state, orientation: action.payload };

    case ACTIONS.SET_TABLE_DIMENSIONS: {
      const { rows: newRows, columns: newColumns } = action.payload;
      const newCells = {};
      const firstRowHeightPct = 0;
      const bodyRows = Math.max(1, newRows - 1);

      for (let row = 0; row < newRows; row++) {
        for (let col = 0; col < newColumns; col++) {
          const id = `${row}-${col}`;
          const isHeader = row === 0;
          const isFooter = false;
          const isFirst = row === 0;

          const existing = state.cells[id];
          newCells[id] = existing
            ? {
                ...existing,
                row,
                col,
                styles: {
                  ...existing.styles,
                  widthPercent: 100 / newColumns,
                  heightPercent: isFirst ? firstRowHeightPct : (100 - firstRowHeightPct) / bodyRows,
                },
                isHeader,
                isFooter,
                isFirst,
              }
            : {
                id,
                row,
                col,
                content: '',
                styles: {
                  fontWeight: (isHeader || isFooter) ? 'bold' : 'normal',
                  backgroundColor: isHeader ? '#f9fafb' : isFooter ? '#f3f4f6' : '#ffffff',
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  padding: 0,
                  textColor: '#000000',
                  fontStyle: 'normal',
                  textDecoration: 'none',
                  widthPercent: 100 / newColumns,
                  heightPercent: isFirst ? firstRowHeightPct : (100 - firstRowHeightPct) / bodyRows,
                },
                isHeader,
                isFooter,
                isFirst,
                colspan: 1,
                rowspan: 1,
                merged: false,
                parentCell: null,
              };
        }
      }

      const columnWidths = normalizeWidths(state.columnWidths, newColumns);
      for (let r = 0; r < newRows; r++) {
        for (let c = 0; c < newColumns; c++) {
          const k = `${r}-${c}`;
          if (newCells[k]) {
            newCells[k] = {
              ...newCells[k],
              styles: { ...(newCells[k].styles || {}), widthPercent: columnWidths[c] },
            };
          }
        }
      }

      return {
        ...state,
        rows: newRows,
        columns: newColumns,
        columnWidths,
        cells: newCells,
        selectedCell: null,
      };
    }

    case ACTIONS.SELECT_CELL:
      return { ...state, selectedCell: action.payload };

    // ------------------- content/style -------------------
    case ACTIONS.UPDATE_CELL_CONTENT: {
      const { cellId, content } = action.payload;
      if (!state.cells[cellId]) return state;
      return {
        ...state,
        cells: {
          ...state.cells,
          [cellId]: { ...state.cells[cellId], content },
        },
      };
    }

    case ACTIONS.UPDATE_CELL_STYLE: {
      const { cellId, styles } = action.payload;
      const cells = { ...state.cells };
      if (!cells[cellId]) return state;
      cells[cellId] = {
        ...cells[cellId],
        styles: { ...cells[cellId].styles, ...styles },
      };
      return { ...state, cells };
    }

    // ------------------- rows/columns -------------------
    case ACTIONS.ADD_ROW: {
      const newRow = state.rows;
      const cells = { ...state.cells };
      for (let col = 0; col < state.columns; col++) {
        const id = `${newRow}-${col}`;
        cells[id] = {
          id,
          row: newRow,
          col,
          content: '',
          styles: {
            fontWeight: 'normal',
            backgroundColor: '#ffffff',
            textAlign: 'left',
            verticalAlign: 'middle',
            padding: 0,
            textColor: '#000000',
            widthPercent: state.columnWidths[col] ?? (100 / state.columns),
          },
          isHeader: false,
          isFooter: false,
          colspan: 1,
          rowspan: 1,
          merged: false,
          parentCell: null,
        };
      }
      return { ...state, rows: state.rows + 1, cells };
    }

    case ACTIONS.REMOVE_ROW: {
      if (state.rows <= 1) return state;
      const lastRow = state.rows - 1;
      const cells = { ...state.cells };
      for (let col = 0; col < state.columns; col++) delete cells[`${lastRow}-${col}`];
      return {
        ...state,
        rows: state.rows - 1,
        cells,
        selectedCell: state.selectedCell?.row === lastRow ? null : state.selectedCell,
      };
    }

    case ACTIONS.ADD_COLUMN: {
      const newCol = state.columns;
      const cells = { ...state.cells };
      for (let row = 0; row < state.rows; row++) {
        const id = `${row}-${newCol}`;
        cells[id] = {
          id,
          row,
          col: newCol,
          content: '',
          styles: {
            fontWeight: row === 0 ? 'bold' : 'normal',
            backgroundColor: row === 0 ? '#f9fafb' : '#ffffff',
            textAlign: 'left',
            verticalAlign: 'middle',
            padding: 0,
            textColor: '#000000',
            widthPercent: 0,
          },
          isHeader: row === 0,
          isFooter: row === state.rows - 1,
          colspan: 1,
          rowspan: 1,
          merged: false,
          parentCell: null,
        };
      }
      const cols = state.columns + 1;
      const columnWidths = normalizeWidths(state.columnWidths, cols);
      for (let r = 0; r < state.rows; r++) {
        for (let c = 0; c < cols; c++) {
          const k = `${r}-${c}`;
          if (cells[k]) {
            cells[k] = {
              ...cells[k],
              styles: { ...(cells[k].styles || {}), widthPercent: columnWidths[c] },
            };
          }
        }
      }
      return { ...state, columns: cols, columnWidths, cells };
    }

    case ACTIONS.REMOVE_COLUMN: {
      if (state.columns <= 1) return state;
      const lastCol = state.columns - 1;
      const cells = { ...state.cells };
      for (let row = 0; row < state.rows; row++) delete cells[`${row}-${lastCol}`];
      const cols = state.columns - 1;
      const columnWidths = normalizeWidths(state.columnWidths.slice(0, cols), cols);
      for (let r = 0; r < state.rows; r++) {
        for (let c = 0; c < cols; c++) {
          const k = `${r}-${c}`;
          if (cells[k]) {
            cells[k] = {
              ...cells[k],
              styles: { ...(cells[k].styles || {}), widthPercent: columnWidths[c] },
            };
          }
        }
      }
      return {
        ...state,
        columns: cols,
        columnWidths,
        cells,
        selectedCell: state.selectedCell?.col === lastCol ? null : state.selectedCell,
      };
    }

    // ------------------- header/footer -------------------
    case ACTIONS.TOGGLE_EXPORT_MODAL:
      return { ...state, showExportModal: !state.showExportModal };

    case ACTIONS.TOGGLE_HEADER_ROW: {
      if (!state.selectedCell) return state;
      const row = state.selectedCell.row;
      const isHeader = state.headerRows.includes(row);
      const headerRows = isHeader ? state.headerRows.filter(r => r !== row) : [...state.headerRows, row];

      const cells = { ...state.cells };
      for (let col = 0; col < state.columns; col++) {
        const id = `${row}-${col}`;
        if (!cells[id]) continue;
        cells[id] = {
          ...cells[id],
          isHeader: !isHeader,
          styles: {
            ...cells[id].styles,
            fontWeight: !isHeader ? 'bold' : 'normal',
            backgroundColor: !isHeader ? '#f9fafb' : '#ffffff',
          },
        };
      }
      return { ...state, headerRows, cells };
    }

    case ACTIONS.TOGGLE_FOOTER_ROW: {
      if (!state.selectedCell) return state;
      const row = state.selectedCell.row;
      const isFooter = state.footerRows.includes(row);
      const footerRows = isFooter ? state.footerRows.filter(r => r !== row) : [...state.footerRows, row];

      const cells = { ...state.cells };
      for (let col = 0; col < state.columns; col++) {
        const id = `${row}-${col}`;
        if (!cells[id]) continue;
        cells[id] = {
          ...cells[id],
          isFooter: !isFooter,
          styles: {
            ...cells[id].styles,
            fontWeight: !isFooter ? 'bold' : 'normal',
            backgroundColor: !isFooter ? '#f3f4f6' : '#ffffff',
          },
        };
      }
      return { ...state, footerRows, cells };
    }
    /* ---------------- MERGE RIGHT (absorb a full vertical strip) ---------------- */
case ACTIONS.MERGE_RIGHT: {
  if (!state.selectedCell) return state;

  const { row, col } = state.selectedCell;
  const cells = { ...state.cells };
  const mainKey = cellKey(row, col);
  const main = cells[mainKey];
  if (!main) return state;

  const currColspan = Math.max(1, main.colspan || 1);
  const currRowspan = Math.max(1, main.rowspan || 1);
  const targetCol = col + currColspan;
  if (!isInside(state, row, targetCol)) return state;

  // ensure the entire vertical strip is mergeable
  for (let r = row; r < row + currRowspan; r++) {
    if (!isMergeableCell(cells, r, targetCol)) return state;
  }

  // absorb that strip
  for (let r = row; r < row + currRowspan; r++) {
    const k = cellKey(r, targetCol);
    cells[k] = { ...cells[k], merged: true, parentCell: mainKey };
  }

  cells[mainKey] = { ...main, colspan: currColspan + 1, rowspan: currRowspan };
  return { ...state, cells };
}

/* ---------------- MERGE DOWN (absorb a full horizontal strip) ---------------- */
case ACTIONS.MERGE_DOWN: {
  if (!state.selectedCell) return state;

  const { row, col } = state.selectedCell;
  const cells = { ...state.cells };
  const mainKey = cellKey(row, col);
  const main = cells[mainKey];
  if (!main) return state;

  const currRowspan = Math.max(1, main.rowspan || 1);
  const currColspan = Math.max(1, main.colspan || 1);
  const targetRow = row + currRowspan;
  if (!isInside(state, targetRow, col)) return state;

  // ensure the entire horizontal strip is mergeable
  for (let c = col; c < col + currColspan; c++) {
    if (!isMergeableCell(cells, targetRow, c)) return state;
  }

  // absorb that strip
  for (let c = col; c < col + currColspan; c++) {
    const k = cellKey(targetRow, c);
    cells[k] = { ...cells[k], merged: true, parentCell: mainKey };
  }

  cells[mainKey] = { ...main, rowspan: currRowspan + 1, colspan: currColspan };
  return { ...state, cells };
}

/* ---------------- SPLIT HORIZONTAL (give back the rightmost column of the block) ---------------- */
case ACTIONS.SPLIT_CELL: {
  if (!state.selectedCell) return state;

  const { row, col } = state.selectedCell;
  const cells = { ...state.cells };
  const key = cellKey(row, col);
  const main = cells[key];
  if (!main) return state;

  const colspan = Math.max(1, main.colspan || 1);
  const rowspan = Math.max(1, main.rowspan || 1);
  if (colspan <= 1) return state;

  const newColspan = colspan - 1;
  const restoreCol = col + newColspan; // rightmost col being released
  for (let r = row; r < row + rowspan; r++) {
    const rk = cellKey(r, restoreCol);
    if (cells[rk]) cells[rk] = { ...cells[rk], merged: false, parentCell: null };
  }
  cells[key] = { ...main, colspan: newColspan };

  return { ...state, cells };
}

/* ---------------- SPLIT VERTICAL (give back the bottom row of the block) ---------------- */
case ACTIONS.SPLIT_CELL_DOWN: {
  if (!state.selectedCell) return state;

  const { row, col } = state.selectedCell;
  const cells = { ...state.cells };
  const key = cellKey(row, col);
  const main = cells[key];
  if (!main) return state;

  const rowspan = Math.max(1, main.rowspan || 1);
  const colspan = Math.max(1, main.colspan || 1);
  if (rowspan <= 1) return state;

  const newRowspan = rowspan - 1;
  const restoreRow = row + newRowspan; // bottom row being released
  for (let c = col; c < col + colspan; c++) {
    const ck = cellKey(restoreRow, c);
    if (cells[ck]) cells[ck] = { ...cells[ck], merged: false, parentCell: null };
  }
  cells[key] = { ...main, rowspan: newRowspan };

  return { ...state, cells };
}

    // ------------------- history -------------------
    case ACTIONS.SAVE_TO_HISTORY: {
      const snapshot = {
        cells: state.cells,
        rows: state.rows,
        columns: state.columns,
        headerRows: state.headerRows,
        footerRows: state.footerRows,
        columnWidths: state.columnWidths,
        addedTable: state.addedTable,
        tableSize: state.tableSize,
        orientation: state.orientation,
        paperType: state.paperType,     // ← add
        type: state.type,
      };
      const newHistory = state.history.slice(0, state.historyIndex + 1).concat(snapshot);
      if (newHistory.length > 50) newHistory.shift();
      return { ...state, history: newHistory, historyIndex: newHistory.length - 1 };
    }

    case ACTIONS.UNDO: {
      if (state.historyIndex <= 0) return state;
      const prev = state.history[state.historyIndex - 1];
      return { ...state, ...prev, historyIndex: state.historyIndex - 1, selectedCell: null };
    }

    case ACTIONS.REDO: {
      if (state.historyIndex >= state.history.length - 1) return state;
      const next = state.history[state.historyIndex + 1];
      return { ...state, ...next, historyIndex: state.historyIndex + 1, selectedCell: null };
    }

    // ------------------- column count & widths -------------------
    case ACTIONS.SET_COLUMN_COUNT: {
      const cols = Math.max(1, Math.min(50, action.payload));
      if (cols === state.columns) return state;

      const cells = { ...state.cells };

      if (cols > state.columns) {
        for (let c = state.columns; c < cols; c++) {
          for (let r = 0; r < state.rows; r++) {
            const id = `${r}-${c}`;
            cells[id] = {
              id, row: r, col: c, content: '',
              styles: { ...(cells[`${r}-0`]?.styles ?? {}), widthPercent: 0 },
              isHeader: r === 0, isFooter: false, isFirst: r === 0,
              colspan: 1, rowspan: 1, merged: false, parentCell: null,
            };
          }
        }
      } else {
        for (let c = cols; c < state.columns; c++) {
          for (let r = 0; r < state.rows; r++) delete cells[`${r}-${c}`];
        }
      }

      const columnWidths = normalizeWidths(state.columnWidths, cols);
      for (let r = 0; r < state.rows; r++) {
        for (let c = 0; c < cols; c++) {
          const k = `${r}-${c}`;
          if (cells[k]) {
            cells[k] = {
              ...cells[k],
              styles: { ...(cells[k].styles || {}), widthPercent: columnWidths[c] },
            };
          }
        }
      }

      return { ...state, columns: cols, columnWidths, cells };
    }

    case ACTIONS.SET_COLUMN_WIDTHS: {
      const incoming = action.payload || [];
      const columnWidths = normalizeWidths(incoming, state.columns);

      const cells = { ...state.cells };
      for (let r = 0; r < state.rows; r++) {
        for (let c = 0; c < state.columns; c++) {
          const k = `${r}-${c}`;
          if (cells[k]) {
            cells[k] = {
              ...cells[k],
              styles: { ...(cells[k].styles || {}), widthPercent: columnWidths[c] },
            };
          }
        }
      }
      return { ...state, columnWidths, cells };
    }

    // ------------------- product table wizard -------------------
    case ACTIONS.OPEN_PRODUCT_TABLE_WIZARD: {
      if (!state.selectedCell || state.addedTable) return state;
      return { ...state, showProductTableWizard: true };
    }
    case ACTIONS.CLOSE_PRODUCT_TABLE_WIZARD: {
      return { ...state, showProductTableWizard: false };
    }
    case ACTIONS.ADD_PRODUCT_TABLE: {
      if (!state.selectedCell) return state;
      if (state.addedTable) return state;

      const { columns: payloadCols } = action.payload || {};
      const row = state.selectedCell.row;
      const col = state.selectedCell.col;
      const cells = { ...state.cells };
      const id = `${row}-${col}`;

      if (cells[id]) {
        const columns =
          Array.isArray(payloadCols) && payloadCols.length
            ? payloadCols.map(c => ({
                col_name: c.col_name,
                width: typeof c.width === 'number' ? `${c.width}%` : c.width,
              }))
            : [
                { col_name: 's.no',   width: '25%' },
                { col_name: 'second', width: '25%' },
                { col_name: 'Third',  width: '25%' },
                { col_name: 'Fourth', width: '25%' },
              ];

        cells[id] = { ...cells[id], productTable: true, columns };
      }

      return { ...state, cells, addedTable: true, showProductTableWizard: false };
    }
    case ACTIONS.DELETE_PRODUCT_TABLE: {
      if (!state.selectedCell) return state;
      if (!state.addedTable) return state; // only delete if one exists

      const { row, col } = state.selectedCell;
      const id = `${row}-${col}`;
      const cells = { ...state.cells };
      const cell = cells[id];

      // ensure you're deleting from a cell that actually has the table
      if (!cell || !cell.productTable) return state;

      cells[id] = {
        ...cell,
        productTable: false,
        columns: [],
      };

      return { ...state, cells, addedTable: false, showProductTableWizard: false };
    }

     case ACTIONS.SET_LABELS: {
      const arr = Array.isArray(action.payload) ? action.payload : [];
      return { ...state, labels: arr };
    }

    case ACTIONS.SET_TYPE: {
      const nextType = typeof action.payload === 'string' ? action.payload : state.type;
      return { ...state, type: nextType };
    }

    case ACTIONS.APPLY_LABEL_TO_CELL: {
      const { cellId, labelId } = action.payload || {};
      if (!cellId || !state.cells[cellId]) return state;
      const cells = { ...state.cells };
      cells[cellId] = { ...cells[cellId], labelId }; // store chosen label metadata
      return { ...state, cells };
    }

    case ACTIONS.LOAD_SNAPSHOT: {
      const snap = action.payload;
      if (!isValidSnapshot(snap)) return state;

      const next = {
        ...state,
        rows: snap.rows,
        columns: snap.columns,
        tableSize: snap.tableSize ?? state.tableSize,
        orientation: snap.orientation ?? state.orientation,
        headerRows: Array.isArray(snap.headerRows) ? snap.headerRows : [],
        footerRows: Array.isArray(snap.footerRows) ? snap.footerRows : [],
        labels: Array.isArray(snap.labels) ? snap.labels : (state.labels || []),
        addedTable: !!snap.addedTable,
        cells: snap.cells || {},
        type: snap.type ?? state.type,
        selectedCell: null,
        paperType: snap.paperType ?? state.paperType,
      };

      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(snapshotFromState(next));
      return { ...next, history: newHistory, historyIndex: newHistory.length - 1 };
    }

    case ACTIONS.SET_PAPER_TYPE:
      return { ...state, paperType: action.payload };

    default:
      return state;
  }
}

// ===================================================================
// Provider + hooks
// ===================================================================
export function TableProvider({ children }) {
  const equal = (n) => Array.from({ length: n }, () => +(100 / n).toFixed(4));

  const [state, dispatch] = useReducer(tableReducer, {
    ...initialState,
    columnWidths: equal(initialState.columns),
    cells: generateInitialCells(initialState.rows, initialState.columns),
  });

  // Save initial state to history
  useEffect(() => {
    dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          dispatch({ type: ACTIONS.UNDO });
        } else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
          e.preventDefault();
          dispatch({ type: ACTIONS.REDO });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Action creators
  const actions = {
    setTableSize: useCallback((size) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_TABLE_SIZE, payload: size });
    }, []),

    setOrientation: useCallback((orientation) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_ORIENTATION, payload: orientation });
    }, []),

    setTableDimensions: useCallback((rows, columns) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_TABLE_DIMENSIONS, payload: { rows, columns } });
    }, []),

    selectCell: useCallback((cell) => {
      dispatch({ type: ACTIONS.SELECT_CELL, payload: cell });
    }, []),

    updateCellContent: useCallback((cellId, content) => {
      dispatch({ type: ACTIONS.UPDATE_CELL_CONTENT, payload: { cellId, content } });
    }, []),

    updateCellStyle: useCallback((cellId, styles) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.UPDATE_CELL_STYLE, payload: { cellId, styles } });
    }, []),

    addRow: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.ADD_ROW });
    }, []),

    removeRow: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.REMOVE_ROW });
    }, []),

    addColumn: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.ADD_COLUMN });
    }, []),

    removeColumn: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.REMOVE_COLUMN });
    }, []),

    toggleExportModal: useCallback(() => {
      dispatch({ type: ACTIONS.TOGGLE_EXPORT_MODAL });
    }, []),

    toggleHeaderRow: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.TOGGLE_HEADER_ROW });
    }, []),

    toggleFooterRow: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.TOGGLE_FOOTER_ROW });
    }, []),

    undo: useCallback(() => dispatch({ type: ACTIONS.UNDO }), []),
    redo: useCallback(() => dispatch({ type: ACTIONS.REDO }), []),
    saveToHistory: useCallback(() => dispatch({ type: ACTIONS.SAVE_TO_HISTORY }), []),

    // merges & unmerge
    mergeRight: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.MERGE_RIGHT });
    }, []),

    mergeDown: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.MERGE_DOWN });
    }, []),

    unmergeCell: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.UNMERGE_CELL });
    }, []),

    splitCell: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SPLIT_CELL });
    }, []),

    splitCellDown: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SPLIT_CELL_DOWN });
    }, []),

    // columns/widths
    setColumnCount: useCallback((n) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_COLUMN_COUNT, payload: n });
    }, []),

    setColumnWidths: useCallback((arr) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_COLUMN_WIDTHS, payload: arr });
    }, []),

    // product table wizard
    addProductTable: useCallback(() => {
      dispatch({ type: ACTIONS.OPEN_PRODUCT_TABLE_WIZARD });
    }, []),

    deleteProductTable: useCallback(() => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.DELETE_PRODUCT_TABLE });
    }, []),


    submitProductTableWizard: useCallback((columns) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.ADD_PRODUCT_TABLE, payload: { columns } });
    }, []),

    closeProductTableWizard: useCallback(() => {
      dispatch({ type: ACTIONS.CLOSE_PRODUCT_TABLE_WIZARD });
    }, []),

    setLabels: useCallback((labels) => {
      dispatch({ type: ACTIONS.SET_LABELS, payload: labels });
    }, []),

    applyLabelToCell: useCallback((cellId, labelId) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.APPLY_LABEL_TO_CELL, payload: { cellId, labelId } });
    }, []),

    setType: useCallback((nextType) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.SET_TYPE, payload: nextType });
    }, []),

    loadSnapshot: useCallback((snapshotObj) => {
      dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
      dispatch({ type: ACTIONS.LOAD_SNAPSHOT, payload: snapshotObj });
    }, []),

    setPaperType: useCallback((paperType) => {
    dispatch({ type: ACTIONS.SAVE_TO_HISTORY });
    dispatch({ type: ACTIONS.SET_PAPER_TYPE, payload: paperType });
  }, []),
  };

  

  const value = { state, actions };
  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}

export function useTable() {
  const ctx = useContext(TableContext);
  if (!ctx) throw new Error('useTable must be used within a TableProvider');
  return ctx;
}
